import { compareStreamIds } from './stream/stream-ids';
import type { NamedTurnTerminalKind } from './turn-operation-registry';

export type TurnBoundaryWitnessPorts = {
  settleNamed: (turnId: string, kind: NamedTurnTerminalKind) => void;
  settleUnnamed: () => void;
};

export type TurnBoundaryWitness = {
  /** Drop cursor high-water and unnamed-cycle state with the run. */
  resetForRunChange: () => void;
  /**
   * Open a new unnamed-stream cycle. Keep the cursor high-water so a stale
   * snapshot of turn A cannot settle turn B.
   */
  armForNewTurn: () => void;
  witnessStream: (
    turnId: string | null,
    cursor: string,
    kind: NamedTurnTerminalKind
  ) => void;
  witnessSnapshot: (cursor: string | null) => void;
};

/**
 * Stream/snapshot terminal arbitration for one run. Named identity and
 * settlement live on the operation registry; this only decides whether a
 * cursor is new enough to call a port.
 *
 * Unnamed stream events settle once per armed cycle. A later unnamed frame
 * with a newer cursor is ignored until the next submit / begin / turn_started
 * / rising activity. Named stream events skip that latch — the registry is
 * idempotent per id.
 */
export const createTurnBoundaryWitness = (
  ports: TurnBoundaryWitnessPorts
): TurnBoundaryWitness => {
  let coveredThrough: string | null = null;
  let unnamedStreamConsumed = false;

  const isStale = (cursor: string | null): boolean =>
    coveredThrough !== null &&
    (cursor === null || compareStreamIds(cursor, coveredThrough) <= 0);

  return {
    resetForRunChange: () => {
      coveredThrough = null;
      unnamedStreamConsumed = false;
    },
    armForNewTurn: () => {
      unnamedStreamConsumed = false;
    },
    witnessStream: (turnId, cursor, kind) => {
      if (isStale(cursor)) return;

      if (turnId !== null) {
        ports.settleNamed(turnId, kind);
      } else if (unnamedStreamConsumed) {
        return;
      } else {
        ports.settleUnnamed();
      }

      coveredThrough = cursor;
      unnamedStreamConsumed = true;
    },
    witnessSnapshot: (cursor) => {
      if (coveredThrough !== null) {
        if (isStale(cursor)) return;
        coveredThrough = cursor;
        unnamedStreamConsumed = true;
        ports.settleUnnamed();
        return;
      }
      unnamedStreamConsumed = true;
      if (cursor !== null) {
        coveredThrough = cursor;
      }
      ports.settleUnnamed();
    },
  };
};
